import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Snackbar from '@mui/material/Snackbar';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import BlockIcon from '@mui/icons-material/Block';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import agent from '../../agent';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

// same reasons as mobile AbuseScreen (API AbuseReason enum) → reportAbuse string keys
export const USER_REPORT_REASONS = [
  ['SPAM', 'spam'],
  ['HARASSMENT', 'harassment'],
  ['HATE_SPEECH', 'hate'],
  ['NUDITY', 'nudity'],
  ['VIOLENCE', 'violence'],
  ['COPYRIGHT', 'copyright'],
  ['MISINFORMATION', 'misinformation'],
  ['OTHER', 'other'],
];

const linkButtonSx = { textTransform: 'none', zIndex: 0, '&:focus': { outline: 'none' } };

/** "Report user" + "Block user"/"Unblock" for someone else's profile (App Store 1.2 / Google Play UGC). */
const UserSafetyActions = ({ profile, onBlockedChange }) => {
  const translation = useTranslation();
  const strings = translation.userBlock;
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState('SPAM');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  const run = async (action, successText) => {
    setBusy(true);
    try {
      await action();
      setToast(successText);
      return true;
    } catch (error) {
      setToast(strings.error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendReport = async () => {
    const ok = await run(
      () => agent.Abuse.report({ reason, description: '', reportedUserId: profile.id }),
      strings.reportedToast,
    );
    if (ok) setReportOpen(false);
  };

  const block = async () => {
    const ok = await run(() => agent.Profile.block(profile.username), strings.blockedToast);
    setBlockOpen(false);
    if (ok) onBlockedChange(true);
  };

  const unblock = async () => {
    const ok = await run(() => agent.Profile.unblock(profile.username), strings.unblockedToast);
    if (ok) onBlockedChange(false);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
      <Button
        size="small"
        startIcon={<FlagOutlinedIcon />}
        sx={{ ...linkButtonSx, color: 'rgba(255,255,255,0.55)' }}
        onClick={() => setReportOpen(true)}
        data-testid="profile-report-user"
      >
        {strings.reportUser}
      </Button>
      <Button
        size="small"
        disabled={busy}
        startIcon={profile.blocked ? <HowToRegOutlinedIcon /> : <BlockIcon />}
        sx={{ ...linkButtonSx, color: profile.blocked ? DEFAULT_THEME_COLOR : '#f87171' }}
        onClick={() => (profile.blocked ? unblock() : setBlockOpen(true))}
        data-testid="profile-block-user"
      >
        {profile.blocked ? strings.unblock : strings.block}
      </Button>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)}>
        <DialogTitle>{strings.reportUser}: {profile.username}</DialogTitle>
        <DialogContent>
          <DialogContentText>{strings.reportUserDescription}</DialogContentText>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {USER_REPORT_REASONS.map(([value, key]) => (
              <FormControlLabel key={value} value={value} control={<Radio />} label={translation.reportAbuse[key]} />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>{strings.cancel}</Button>
          <Button disabled={busy} onClick={sendReport} sx={{ color: DEFAULT_THEME_COLOR }} data-testid="report-user-send">
            {strings.send}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={blockOpen} onClose={() => setBlockOpen(false)}>
        <DialogTitle>{strings.blockConfirmTitle.replace('{username}', profile.username)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{strings.blockConfirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockOpen(false)}>{strings.cancel}</Button>
          <Button disabled={busy} onClick={block} sx={{ color: '#f44336' }} data-testid="block-user-confirm">
            {strings.blockConfirm}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default UserSafetyActions;
